import { Component, OnInit, ViewChild} from '@angular/core';
import { ApplicationStateService } from '../../../../services/application.state.service';
import { DoorService } from '../../../../services/door/door.service';
import { CustomerService } from '../../../../services/customer/customer.service';
import { TrackingService } from '../../../../services/tracking.service';
import {NgbModal, NgbTooltip} from "@ng-bootstrap/ng-bootstrap";
import {ScenarioType} from "../../../../models/scenario-image";
import {lastValueFrom, map, take} from "rxjs";
import {Product} from "../../../../models/product";
import {ProductLinkType} from "../../../../../generated/graphql";
import {IframeModalComponent} from "../../../iframe-modal.component";
import {doDownload} from "../../../../utils/download.visualization.helper";
import { VisualizeEngine } from '@door-vision/visualisation-engine';
import {TranslateService} from "@ngx-translate/core";
import { share } from '../../../../utils/device.utils';

@Component({
  selector: 'app-visualize-footer-panel',
  templateUrl: './visualize-footer.component.html'
})
export class VisualizeFooterComponent implements OnInit {

  @ViewChild('refineCornersTooltip') public refineCornersTooltip!: NgbTooltip;

  constructor(public applicationStateService: ApplicationStateService,
              private customerService: CustomerService,
              protected visualizeEngine: VisualizeEngine,
              private modalService: NgbModal,
              private trackingService: TrackingService,
              protected translationService: TranslateService,
              private doorService: DoorService,
  ) {
  }

  public customerConfig$ = this.customerService.getCustomerConfig$()
  selectedDoorAtCurrentIndex$ = this.doorService.getSelectedDoorAtCurrentIndex$()

  ngOnInit(): void {
    this.applicationStateService.getHasUploadedImage().subscribe((hasUploadedImage: boolean) => {
      try {
        if (this.refineCornersTooltip) {
          this.refineCornersTooltip.open()

          // Show tooltip for 4 seconds
          setTimeout(() => {
            try {
              this.refineCornersTooltip.close(true);
            } catch (e) {
              console.info('Could not close tooltip', e)
            }
          }, 4000)
        }
      } catch (e) {
        console.info('Could not open tooltip', e)
      }
    })
  }

  toggleFavorite(product: Product): void {
    this.doorService.toggleFavorite(product);
    this.trackingService.track({
      event: 'toggleFavorite',
      body: {
        selectedDoorId: product.id,
        selectedExternalDoorId: product.externalIds[0],
        isFavorite: product.isFavorite,
        location: 'buttonGroup'
      }
    }).then();
  }

  flipDoor(): void {
    const index = this.doorService.getSelectedDoorCornersIndexValue()
    this.visualizeEngine.flipDoor(index);
    this.trackingService.track({event: 'flipDoor'}).then();
  }

  isShowingExampleImage(): boolean {
    return this.visualizeEngine.getCurrentScenarioValue()?.scenarioImage.type === ScenarioType.EXAMPLE;
  }

  refineCorners(): void {
    this.applicationStateService.setRefineMode("EDIT")
  }

  async openHomepage(): Promise<void> {
    const homepageUrl = await this.customerService.getCustomerHomepageUrl().pipe(take(1)).toPromise()
    if (homepageUrl) {
      window.open(homepageUrl, '_blank');

      this.trackingService.track({event: 'openHomepageButtonClicked'}).then();
    }
  }

  openProductUrl(product: Product): void {
    this.customerConfig$.subscribe((customerConfig) => {

      // Either use the shop link overwrite or the product page url from the door
      const shopLinkOverwrite = customerConfig.shopLinkOverwrite
      const url = shopLinkOverwrite && shopLinkOverwrite.length !== 0 ? shopLinkOverwrite : product.link

      if (!url) {
        return
      }

      let productPageUrl = url
        .replace(/<EXTERNAL_ID>/g, product.externalIds[0])
        .replace(/<URL>/g, encodeURIComponent(window.location.href))
        .replace(/<DOOR_NAME>/g, encodeURIComponent(product.name))

      this.trackingService.track({
        event: 'productLinkClicked',
        body: {selectedDoorId: product.id, selectedExternalDoorId: product.externalIds[0]}
      }).then();

      switch (customerConfig.productLinkType) {
        case ProductLinkType.NewTab:
          window.open(productPageUrl, '_blank');
          break;
        case ProductLinkType.SameTab:
          window.open(productPageUrl, '_self');
          break;
        case ProductLinkType.PopUp:
          const modal = this.modalService.open(IframeModalComponent, {
            centered: true,
            windowClass: 'iframe-modal',
            scrollable: false,
            fullscreen: "lg"
          })
          modal.componentInstance.url = productPageUrl
          modal.componentInstance.title = product.name
          break;
      }
    })
  }

  async onDownload(): Promise<void> {
    const originalSizeCanvas = await this.visualizeEngine.getVisualizationInOriginalSize();
    const logoUrl = this.customerService.getCustomerConfig()?.logoUrl;
    const currentDoor = this.doorService.getSelectedDoorAtCurrentIndex();

    if (originalSizeCanvas) {
      await doDownload(originalSizeCanvas, logoUrl, currentDoor?.name || 'door', {
        noOverlays: false
      });
    }

    this.trackingService.track({event: 'downloadScenario'}).then();
  }

  async share(): Promise<void> {
    // Build the sharing data
    const url = window.location.href;
    const title = await lastValueFrom(this.translationService.get('redesign.sharing.title'));
    const text = await lastValueFrom(this.translationService.get('redesign.sharing.text'));
    const copyMessageText = await lastValueFrom(this.translationService.get('redesign.sharing.copyMessage'));
    const sharingData: ShareData = {
      url,
      title,
      text
    };

    await share(sharingData, copyMessageText);

    // Track the sharing action
    this.trackingService.track({event: 'shareScenario'}).then();
  }

}
